/**
 * Person Map Controller
 * Handles the biography map initialization and integration with BiographyMapAnimation
 */

class PersonMapController {
  constructor(fragmentLocations, relatedSpaceMarkers, firstFragmentLocation) {
    this.fragmentLocations = fragmentLocations;
    this.relatedSpaceMarkers = relatedSpaceMarkers;
    this.firstFragmentLocation = firstFragmentLocation;
    this.map = null;
    this.biographyAnimation = null;
  }

  /**
   * Initialize the map and animation system
   */
  init() {
    // Check if mapboxgl is available
    if (typeof mapboxgl === 'undefined') {
      console.error('Mapbox GL JS not loaded - biography map will not work');
      document.getElementById('biographyMap').innerHTML =
        '<div style="display: flex; align-items: center; justify-content: center; height: 400px; background: #f5f5f5; color: #666; font-size: 16px;">Map unavailable - please check Content Security Policy</div>';
      return;
    }

    // Initialize the map
    this.initializeMap();
  }

  /**
   * Initialize Mapbox map
   */
  initializeMap() {
    // Clear the map container to ensure it's empty for Mapbox GL
    const mapContainer = document.getElementById('biographyMap');
    mapContainer.innerHTML = '';

    // Set up Mapbox access token
    mapboxgl.accessToken = 'pk.eyJ1Ijoic3RvcnlieW51bWJlcnMiLCJhIjoiY2t4OWVzYTZ0MHlpczJ2bW5lM3FmbTN1ZyJ9.ivYDwvqV_HUQtV_IfmNa_Q';

    // Create the map
    this.map = new mapboxgl.Map({
      container: 'biographyMap',
      // Reuse the same base map style as the /biographies overview map
      style: 'mapbox://styles/mapbox/light-v11',
      center: [this.firstFragmentLocation.lng, this.firstFragmentLocation.lat],
      zoom: 7,
      scrollZoom: false,
      projection: 'globe' // Use globe projection for better visualization
    });

    // Set up map event handlers
    this.setupMapEventHandlers();
  }

  /**
   * Setup map event handlers
   */
  setupMapEventHandlers() {
    this.map.on('load', () => {
      console.log('Map loaded, initializing biography animation...');

      // Initialize biography animation first so controls can bind to it
      this.initializeBiographyAnimation();

      // Add related space markers
      this.addRelatedSpaceMarkers();

      // Fit initial bounds
      this.fitInitialBounds();

      // Add controls after animation setup to ensure listeners bind
      this.addMapControls();
    });

    // Handle animation completion event
    window.addEventListener('biographyAnimationComplete', () => {
      this.onAnimationComplete();
    });
  }

  /**
   * Add map controls
   */
  addMapControls() {
    if (window.BiographyMapAnimation && window.BiographyMapAnimation.getControlsHTML) {
      const controlsHtml = window.BiographyMapAnimation.getControlsHTML();
      const controlsContainer = document.getElementById('biographyMapControls');
      if (controlsContainer) {
        controlsContainer.innerHTML = controlsHtml;
        this.setupControlEventListeners();
        // Ensure zoom buttons are wired after DOM insertion
        if (this.biographyAnimation && typeof this.biographyAnimation.setupZoomControls === 'function') {
          this.biographyAnimation.setupZoomControls();
        }
      }
    }
  }

  /**
   * Setup control event listeners
   */
  setupControlEventListeners() {
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        if (this.biographyAnimation) this.biographyAnimation.play();
      });
    }

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        if (this.biographyAnimation) this.biographyAnimation.pause();
      });
    }

    // Forward/back buttons (mobile only)
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (this.biographyAnimation) this.biographyAnimation.navigateToPrevious();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (this.biographyAnimation) this.biographyAnimation.navigateToNext();
      });
    }
  }

  /**
   * Initialize biography animation with retry logic
   */
  initializeBiographyAnimation() {
    let retries = 0;
    const maxRetries = 20; // Up to 4 seconds total
    const retryDelay = 200;

    const initAnimation = () => {
      if (window.BiographyMapAnimation) {
        console.log('BiographyMapAnimation found, initializing...');

        // Create animation instance
        this.biographyAnimation = new window.BiographyMapAnimation(this.map, this.fragmentLocations);
        this.biographyAnimation.init();

        return true; // Success
      } else {
        retries++;
        if (retries < maxRetries) {
          console.log(`BiographyMapAnimation not yet available, retrying... (${retries}/${maxRetries})`);
          setTimeout(initAnimation, retryDelay);
        } else {
          console.error('BiographyMapAnimation not available after retries - biography animation disabled');
        }
        return false;
      }
    };

    // Start initialization with retry logic
    initAnimation();
  }

  /**
   * Add related space markers to the map
   */
  addRelatedSpaceMarkers() {
    this.relatedSpaceMarkers.forEach((space) => {
      // Create custom blue circle element for marker
      const el = document.createElement('div');
      el.className = 'related-space-marker';
      el.style.width = '16px';
      el.style.height = '16px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#3B82F6';
      el.style.border = '2px solid white';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      el.style.cursor = 'pointer';

      const popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'related-space-popup'
      }).setHTML(`
        <div class="uk-text-center uk-padding-small">
          <h5 class="uk-margin-remove-bottom related-space-title">
            <a href="${space.url}" style="text-decoration: none;">${space.title}</a>
          </h5>
        </div>
      `);

      // Use custom marker with blue circle
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([space.lng, space.lat])
        .setPopup(popup);

      marker.addTo(this.map);
    });
  }

  /**
   * Fit initial bounds to show all markers
   */
  fitInitialBounds() {
    if (this.fragmentLocations.length > 0 || this.relatedSpaceMarkers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      this.fragmentLocations.forEach(f => bounds.extend([f.lng, f.lat]));
      this.relatedSpaceMarkers.forEach(s => bounds.extend([s.lng, s.lat]));

      // Fit to show all markers with padding
      this.map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000
      });
    }
  }

  /**
   * Handle animation completion
   */
  onAnimationComplete() {
    // Fit bounds to include all markers (biography fragments + related spaces)
    if (this.fragmentLocations.length > 0 || this.relatedSpaceMarkers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      this.fragmentLocations.forEach(f => bounds.extend([f.lng, f.lat]));
      this.relatedSpaceMarkers.forEach(s => bounds.extend([s.lng, s.lat]));

      // Zoom out to show all markers with generous padding
      this.map.fitBounds(bounds, {
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        duration: 3000,
        maxZoom: 8
      });
    }
  }

  /**
   * Setup space hover interactions
   */
  setupSpaceHoverInteractions() {
    let startCenter, startZoom;

    setTimeout(() => {
      if (this.map) {
        this.map.resize();
        startCenter = this.map.getCenter();
        startZoom = this.map.getZoom();
      }
    }, 500);

    $('.mapped-link').hover(
      function() {
        if (this.map) {
          const lng = $(this).data('lng');
          const lat = $(this).data('lat');
          const title = $(this).text();

          // Fly to location faster and zoom closer
          this.map.flyTo({
            center: [lng, lat],
            zoom: 12,
            speed: 1.2,
            essential: true
          });

          // Create and show popup with space name
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'hover-popup'
          })
            .setLngLat([lng, lat])
            .setHTML(`<div style="font-weight: bold; padding: 4px 8px;">${title}</div>`)
            .addTo(this.map);

          // Store popup reference for removal
          $(this).data('popup', popup);
        }
      }.bind(this),
      function() {
        if (this.map) {
          // Remove popup
          const popup = $(this).data('popup');
          if (popup) {
            popup.remove();
          }

          // Return to original view
          this.map.flyTo({
            center: startCenter,
            zoom: startZoom,
            speed: 1.0,
            essential: true
          });
        }
      }.bind(this)
    );
  }
}

export default PersonMapController;
